import { normalizeOrderStatus, getOrderStatusLabel } from "../../utils/orderStatus";
import { useEffect, useState, useRef, useCallback } from "react"
import { useSearchParams, useNavigate } from "react-router-dom"
import { Loader2, CheckCircle2, XCircle, Smartphone, ArrowRight } from "lucide-react"
import { Order } from "../../utils/database"
import { getPawapayError } from "../../utils/getPawapayErrors" // Assure-toi d'ajuster le chemin de ton fichier
import { orderService } from "../../services/orderService"

export default function OrderPendingPage() {
    const [searchParams] = useSearchParams()
    const navigate = useNavigate()
    const orderId = searchParams.get("orderId")

    const [order, setOrder] = useState<Order | null>(null)
    const [loading, setLoading] = useState(true)
    const [status, setStatus] = useState<Order["status"]>("PENDING")
    const [fetchError, setFetchError] = useState<string | null>(null)

    const statusRef = useRef(status)
    statusRef.current = status

    const checkAndSyncStatus = useCallback(async () => {
        if (!orderId) return

        try {
            const updatedOrder = await orderService.syncOrderStatus(orderId)

            if (!updatedOrder) {
                setFetchError("Commande introuvable")
                setLoading(false)
                return
            }

            setOrder(updatedOrder.order)
            setLoading(false)

            const currentStatus = normalizeOrderStatus(updatedOrder.order?.status ?? updatedOrder.status)
            if (!currentStatus) throw new Error("Statut de commande inconnu")
            setFetchError(null)
            setStatus(currentStatus)
        } catch (error) {
            console.error("Erreur synchro Cabupay:", error)
            setFetchError("La vérification du paiement est indisponible. Ne relancez pas le paiement ; consultez à nouveau cette commande.")
            setLoading(false)
        }
    }, [orderId])

    useEffect(() => {
        if (!orderId) {
            navigate("/", { replace: true })
            return
        }

        let isMounted = true

        checkAndSyncStatus()

        const interval = setInterval(() => {
            if (isMounted && statusRef.current === "PENDING") {
                checkAndSyncStatus()
            } else {
                clearInterval(interval)
            }
        }, 3000)

        return () => {
            isMounted = false
            clearInterval(interval)
        }
    }, [orderId, checkAndSyncStatus, navigate])

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <div className="text-center">
                    <Loader2 className="h-10 w-10 text-primary animate-spin mx-auto mb-4" />
                    <p className="text-gray-600 font-medium">Vérification de la commande...</p>
                </div>
            </div>
        )
    }

    if (fetchError) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <div className="max-w-md w-full bg-white rounded-2xl border p-8 shadow-sm text-center">
                    <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                    <h2 className="text-xl font-bold text-gray-900 mb-2">{fetchError}</h2>
                    <p className="text-gray-600 text-sm mb-6">
                        Impossible de récupérer les détails de cette transaction.
                    </p>
                    <button
                        onClick={() => navigate("/")}
                        className="w-full bg-gray-900 text-white py-3 rounded-lg font-medium hover:bg-gray-800 transition"
                    >
                        Retour à l'accueil
                    </button>
                </div>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
            <div className="max-w-md w-full bg-white rounded-2xl border p-8 shadow-sm text-center">
                
                {order && <p className="mb-4 text-xs text-gray-500 break-all">Référence de suivi : {order.id || order._id}</p>}
                {/* PENDING */}
                {status === "PENDING" && (
                    <div>
                        <div className="relative w-20 h-20 mx-auto mb-6 flex items-center justify-center">
                            <div className="absolute inset-0 bg-primary/10 rounded-full animate-ping opacity-75" />
                            <div className="relative bg-primary/20 p-4 rounded-full">
                                <Loader2 className="h-10 w-10 text-primary animate-spin" />
                            </div>
                        </div>

                        <h2 className="text-2xl font-bold text-gray-900 mb-2">
                            Paiement en cours
                        </h2>
                        <p className="text-gray-600 text-sm mb-6">
                            {order?.paymentInitiationState === "unknown" || order?.paymentInitiationState === "starting" ? <>La demande est en cours de vérification. Si vous avez déjà validé ou été débité, ne relancez pas le paiement.</> : <>Veuillez valider la demande Mobile Money sur votre téléphone (<b>{order?.paymentPhone || order?.phoneNumber}</b>).</>}
                        </p>

                        <div className="bg-gray-50 rounded-xl p-4 text-left space-y-3 mb-6 border">
                            <div className="flex justify-between text-sm">
                                <span className="text-gray-500">Réseau d'unités :</span>
                                <span className="font-semibold text-gray-900">{order?.network}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-gray-500">Numéro à recharger :</span>
                                <span className="font-semibold text-gray-900">{order?.phoneNumber}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-gray-500">Montant :</span>
                                <span className="font-semibold text-primary">
                                    {order?.price ? Number(order.price).toLocaleString("fr-FR") : 0} {order?.currency || "FCFA"}
                                </span>
                            </div>
                        </div>

                        <div className="flex items-center justify-center gap-2 text-xs text-gray-400">
                            <Smartphone className="h-4 w-4" />
                            <span>Vérification automatique de la validation...</span>
                        </div>
                    </div>
                )}

                {/* COMPLETED */}
                {(status === "COMPLETED" || status === "DELIVERED") && (
                    <div>
                        <div className="h-20 w-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                            <CheckCircle2 className="h-10 w-10 text-green-600" />
                        </div>

                        <h2 className="text-2xl font-bold text-gray-900 mb-2">
                            {status === "DELIVERED" ? "Commande livrée" : "Paiement confirmé !"}
                        </h2>
                        <p className="text-gray-600 text-sm mb-6">
                            {status === "DELIVERED" ? <>La livraison de vos unités sur le numéro <b>{order?.phoneNumber}</b> a été confirmée.</> : <>Votre paiement est confirmé. Le vendeur doit maintenant livrer vos unités sur le numéro <b>{order?.phoneNumber}</b>.</>}
                        </p>

                        <button
                            onClick={() => navigate("/")}
                            className="w-full bg-primary text-white py-3 rounded-lg font-medium hover:bg-primary/90 transition flex items-center justify-center gap-2"
                        >
                            <span>Retour à l'accueil</span>
                            <ArrowRight className="h-4 w-4" />
                        </button>
                    </div>
                )}

                {(status === "CANCELLED" || status === "REFUNDED") && <div><XCircle className="h-12 w-12 text-gray-500 mx-auto mb-4" /><h2 className="text-2xl font-bold mb-3">Commande {getOrderStatusLabel(status).toLowerCase()}</h2><p className="text-gray-600 mb-6">{order?.reversalReason || "Cette commande est clôturée."}</p><button onClick={() => navigate("/")} className="w-full bg-gray-900 text-white py-3 rounded-lg">Retour à l’accueil</button></div>}

                {/* FAILED */}
                {status === "FAILED" && (
                    <div>
                        <div className="h-20 w-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                            <XCircle className="h-10 w-10 text-red-600" />
                        </div>

                        <h2 className="text-2xl font-bold text-gray-900 mb-2">
                            Transaction échouée
                        </h2>
                        
                        <div className="bg-red-50 border border-red-100 text-red-700 p-3 rounded-lg text-sm mb-6">
                        {getPawapayError(order?.failureCode || order?.failureReason || "")}
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={() => navigate(-1)}
                                className="flex-1 py-3 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition"
                            >
                                Réessayer
                            </button>
                            <button
                                onClick={() => navigate("/")}
                                className="flex-1 bg-gray-900 text-white py-3 rounded-lg font-medium hover:bg-gray-800 transition"
                            >
                                Accueil
                            </button>
                        </div>
                    </div>
                )}

            </div>
        </div>
    )
}